<script setup>
import { computed, ref, onBeforeMount } from 'vue'
import { useStore } from "vuex";
import formService from "@/services/form";
import { useQuery } from '@tanstack/vue-query'
import { HEALTH_WELLNESS_FORM_NAME, PASSPORT_FORM_NAME } from '../constants'
import Spinner from "../../helpers/Spinner.vue";
import PassportsForm from "./PassportsForm.vue"
import SecurityCheck from "../SecurityCheck.vue"
import { hasMFAFormsEnabled } from "@/composables/authorization.js";
import { datadogLogs } from '@datadog/browser-logs'
import { useToast } from "vue-toast-notification";
import { ERROR_TIMEOUT } from "@/constants.js";

const store = useStore();
const passedSecurity = ref(false);
const toast = useToast();

const applicationId = computed(() => store.state.currentApplicationId);

// Query
const { isPending, data: formExistsData, isFetching } = useQuery({
  queryKey: ['form', PASSPORT_FORM_NAME, 'exists', applicationId],
  queryFn: async () => {
    try {
      const data = await formService.submissionExists(PASSPORT_FORM_NAME, { "data.application_id": applicationId.value })
      return data._id
    } catch (error) {
      if (error.response.status !== 404) {
        datadogLogs.logger.error(
          `Error while checking if ${PASSPORT_FORM_NAME} from formio exists`,
          error
        );
        toast.open({
          message: `Error while checking ${PASSPORT_FORM_NAME}`,
          type: "error",
          position: "bottom",
          duration: ERROR_TIMEOUT,
        });
      }
      return ""
    }
  },
  enabled: hasMFAFormsEnabled.value,
  initialData: "",
  gcTime: 0
})

const showPassportForm = computed(() => {
  return !hasMFAFormsEnabled.value || !formExistsData.value || passedSecurity.value
})

const formLastUpdatedDate = computed(() => {
  const currentApplicationSubmissions = store.getters.getCurrentApplicationSubmissions(PASSPORT_FORM_NAME);
  return currentApplicationSubmissions?.find((val => val?._id === formExistsData.value))?.modified;
});

const enableSubmissionFetch = computed(() => !!hasMFAFormsEnabled.value && !formLastUpdatedDate.value && !!formExistsData.value)
const { isPendingSubmission, data: formSubmissionData, isFetchingSubmission } = useQuery({
  queryKey: ['form', PASSPORT_FORM_NAME, 'submission', formExistsData],
  queryFn: async () => {
    try {
      const data = await formService.getSubmission(PASSPORT_FORM_NAME, formExistsData.value)
      return data.modified
    } catch (error) {
      datadogLogs.logger.error(
        `Error while fetching ${PASSPORT_FORM_NAME} from formio`,
        error
      );
      toast.open({
        message: `Error while fetching ${PASSPORT_FORM_NAME}`,
        type: "error",
        position: "bottom",
        duration: ERROR_TIMEOUT,
      });
      return ""
    }
  },
  enabled: enableSubmissionFetch,
  initialData: "",
  gcTime: 0
})

const lmt = computed(() => {
  if(formLastUpdatedDate.value) {
    return formLastUpdatedDate.value;
  } else if (formSubmissionData.value) {
    return formSubmissionData.value;
  } else {
    return undefined;
  }
})

onBeforeMount(() => passedSecurity.value = false);
</script>

<template>
  <div>
    <SecurityCheck
      v-if="formExistsData && !passedSecurity && lmt"
      :application-id="applicationId"
      form-title="Passport"
      :form-last-updated-date="lmt"
      @update-form="() => passedSecurity = true"
    />
    <PassportsForm
      v-else-if="!isFetching && !isFetchingSubmission && !isPendingSubmission && showPassportForm"
      :input-submission-id="formExistsData"
    />
    <Spinner v-else />
  </div>
</template>

<style scoped>

</style>
